/// <reference lib="es2015" />
import apiClient from '../apiClient';
import { getSaleStatusLabel, getSaleStatusColor } from '../../../utils/status.utils';
import { SaleStatus, PaymentStatus, PaymentType, FulfillmentMethod, SETTLED_BEFORE_DISPATCH_PAYMENT_TYPES } from '../types';
import type { 
  LineItemFullRead,
  PaymentRead,
  SaleDetailRead,
  SaleListItem,
  SaleCreate,
  SaleStatusUpdate,
  PaymentCreate,
  OnlineOrderCreate,
  PickupReservationCreate,
  SaleCompositeUpdatePayload,
  SaleDiscountPayload,
  SaleCancelRefundPayload,
  PackageCreate,
  ShippingOptions
} from '../types';

export interface ReceptionLineItem {
  line_item_id: number;
  component_id: number;
  sku: string | null;
  quantity: number;
  sale_price: number;
  description: string | null;
  location: string | null;
  warehouse_area_id: number | null;
}

export interface PaymentInSaleCreate {
  payment_type: PaymentType;
  amount: number;
  status?: PaymentStatus;
}

export interface CreateReceptionSalePayload {
  contact_id: string;
  company_id?: string | null;
  quote_id?: string | null;
  line_item_ids: number[];
  supplier: 'Terra Cycle SRL' | 'Zlotea Adrian II';
  discount_amount?: number;
  discount_comment?: string | null;
  sale_reference?: string;
  shipping_address_id?: string | null;
  invoice_address_id?: string | null;
  payments?: PaymentInSaleCreate[];
  issue_invoice?: boolean;
  issue_payment_doc?: boolean;
  // 'delivery' = paid at the counter, shipped by courier. Requires
  // shipping_address_id and `shipment`, and the backend only accepts cash /
  // card POS for it — the money has to be in the till before the parcel goes.
  fulfillment_method?: 'pickup_in_store' | 'delivery';
  shipment?: {
    provider_id: number;
    packages: PackageCreate[];
    fee_payer?: 'supplier' | 'customer';
    estimated_shipping_cost?: number;
    shipping_options?: ShippingOptions;
  };
}

export interface SkuReceptionSaleSkuPayload {
  sku: string;
  quantity: number;
  price: number;
  currency_id?: number;
  component_id?: number | null;
}

export interface CreateSkuReceptionSalePayload {
  contact_id: string | number;
  company_id?: string | null;
  supplier: 'Terra Cycle SRL' | 'Zlotea Adrian II';
  discount_amount?: number;
  discount_comment?: string | null;
  sale_reference?: string;
  shipping_address_id?: string | null;
  invoice_address_id?: string | null;
  currency_id?: number;
  payments?: PaymentInSaleCreate[];
  skus: SkuReceptionSaleSkuPayload[];
}

export interface CreateReceptionSwapPayload {
  contact_id: string | number;
  outgoing_line_item_id: number;
  outgoing_quantity: number;
  incoming_line_item_id: number;
  incoming_quantity: number;
  original_sale_id: string | number;
  reason: string;
}

export interface ReceptionSwapResult {
  original_sale_hash: string;
  outgoing_line_item_id: number;
  incoming_line_item_id: number;
  stock_deducted: boolean;
}

// Interfaces for API communication
export interface ListSalesParams {
  contact_id?: string;
  company_id?: string;
  limit?: number;
  offset?: number;
  status?: SaleStatus;
  created_from?: string; // ISO date or datetime
  created_to?: string;   // ISO date or datetime
  payment_status?: string; // Optional payment status filter (e.g., 'paid')
  // Filter by external partner order identifier (e.g., Ovoko order ID)
  sale_partner_order_id?: string;
  // Filter by sales channel partner ID
  sale_partner_id?: number;
  sale_hash_id?: string;
  fulfillment_method?: 'pickup_in_store' | 'delivery';
  count_only?: boolean;
  include_line_items?: boolean;
  include_shipment_payment_invoice?: boolean;
  sort_by?: 'created_at' | 'updated_at' | 'status';
  sort_order?: 'asc' | 'desc';
}

export interface PaginatedSales {
  total: number;
  items: SaleListItem[];
  limit: number;
  offset: number;
}

export interface CountResponse {
  count: number;
}

// Valid status transitions
const VALID_STATUS_TRANSITIONS: Record<SaleStatus, SaleStatus[]> = {
  [SaleStatus.NEW]: [SaleStatus.COLLECTING, SaleStatus.CANCELLED],
  [SaleStatus.AWAITING_BUYER_CONFIRMATION]: [SaleStatus.COLLECTING, SaleStatus.ON_HOLD, SaleStatus.CANCELLED],
  [SaleStatus.COLLECTING]: [SaleStatus.PACKING, SaleStatus.COMPLETED, SaleStatus.ON_HOLD, SaleStatus.CANCELLED],
  [SaleStatus.PACKING]: [SaleStatus.READY_TO_SHIP, SaleStatus.ON_HOLD, SaleStatus.CANCELLED],
  [SaleStatus.READY_TO_SHIP]: [SaleStatus.SHIPPED, SaleStatus.ON_HOLD, SaleStatus.CANCELLED],
  [SaleStatus.SHIPPED]: [SaleStatus.DELIVERED, SaleStatus.ON_HOLD, SaleStatus.CANCELLED],
  [SaleStatus.DELIVERED]: [],
  [SaleStatus.COMPLETED]: [],
  [SaleStatus.ON_HOLD]: [
    SaleStatus.NEW,
    SaleStatus.COLLECTING,
    SaleStatus.PACKING,
    SaleStatus.READY_TO_SHIP,
    SaleStatus.SHIPPED,
    SaleStatus.COMPLETED,
    SaleStatus.CANCELLED
  ],
  [SaleStatus.CANCELLED]: []
};

// Helper function to validate status transitions
export function isValidStatusTransition(fromStatus: SaleStatus, toStatus: SaleStatus): boolean {
  const allowed = VALID_STATUS_TRANSITIONS[fromStatus] || [];
  return allowed.indexOf(toStatus) !== -1;
}

// API Functions

/**
 * List sales with optional filters
 */
export async function listSales(params: ListSalesParams = {}): Promise<PaginatedSales | CountResponse> {
  return await apiClient.get('/crm/sales', { params });
}

export async function getSalesCount(params: ListSalesParams = {}): Promise<number> {
  const result = await listSales({ ...params, count_only: true });
  if (result && 'count' in result && typeof result.count === 'number') {
    return result.count;
  }
  if (result && 'total' in result && typeof (result as PaginatedSales).total === 'number') {
    return (result as PaginatedSales).total;
  }
  return 0;
}

export async function getStatusCounts(): Promise<Record<string, number>> {
  return await apiClient.get('/crm/sales/status_counts');
}

export interface SalesPartnerOption {
  id: number;
  name: string;
}

export async function listSalesPartners(): Promise<SalesPartnerOption[]> {
  return await apiClient.get('/crm/sales/partners');
}

/**
 * Get complete sale details
 */
export async function getSaleDetails(saleHash: string): Promise<SaleDetailRead> {
  return await apiClient.get(`/crm/sales/${saleHash}`);
}

export interface StaleFulfillmentAlert {
  sale_id: string;        // hash id, e.g. "S-XXXX"
  status: string;         // 'collecting' | 'packing'
  created_at: string | null;
  age_days: number | null;
  customer: string | null;
}

/**
 * Sales stuck in collecting/packing older than 7 days. Returns [] for users
 * who are not owner/admin/call_center. Surfaced as urgent bell alerts (no task).
 */
export async function getStaleFulfillmentAlerts(): Promise<StaleFulfillmentAlert[]> {
  return await apiClient.get('/crm/sales/stale-fulfillment-alerts');
}

export interface RefundNeededAlert {
  sale_id: string;        // hash id, e.g. "S-XXXX"
  status: string;         // 'cancelled'
  created_at: string | null;
  age_days: number | null;
  customer: string | null;
}

/**
 * Cancelled sales with a collected payment → money must be refunded. Returns []
 * for users who are not owner/finance_manager. Surfaced as urgent (red) bell
 * alerts (no task); clears automatically once the sale is refunded/un-cancelled.
 */
export async function getRefundNeededAlerts(): Promise<RefundNeededAlert[]> {
  return await apiClient.get('/crm/sales/refund-needed-alerts');
}

/**
 * Create a new reception sale
 */
export async function createReceptionSale(saleData: CreateReceptionSalePayload): Promise<SaleDetailRead> {
  return await apiClient.post('/crm/sales/reception', saleData, {
    // Google Sheets sync can take longer; keep a single request attempt and wait longer.
    maxRetries: 1,
    timeoutMs: 5 * 60 * 1000,
  });
}

export async function createSkuReceptionSale(saleData: CreateSkuReceptionSalePayload): Promise<SaleDetailRead> {
  return await apiClient.post('/crm/sales/sku-reception', saleData);
}

export async function createReceptionSwap(payload: CreateReceptionSwapPayload): Promise<ReceptionSwapResult> {
  return await apiClient.post('/crm/sales/reception-swap', payload, { maxRetries: 0 });
}

/**
 * Create a new online order sale
 */
export async function createOnlineOrder(saleData: OnlineOrderCreate): Promise<SaleDetailRead> {
  return await apiClient.post('/crm/sales/online-order', saleData);
}

/**
 * VAT + currency context for a destination country (order drawer).
 * Returns { country_code, vat_rate, suggested_currency_id, bnr: { eur_rate, rate_date } }.
 */
export async function getPricingContext(countryCode: string, hasCompany: boolean): Promise<{
  country_code: string;
  vat_rate: number;
  suggested_currency_id: number;
  bnr: { eur_rate: number | null; rate_date: string | null };
}> {
  return await apiClient.get('/crm/sales/pricing-context', {
    params: { country_code: countryCode, has_company: hasCompany },
  });
}

/**
 * Convert an existing sale to another currency (BNR previous-day rate).
 * Refused by BE while the sale has a non-reversed invoice.
 */
export async function changeSaleCurrency(saleHash: string, currencyId: number): Promise<{
  changed: boolean; rate?: number; message?: string;
}> {
  return await apiClient.post(`/crm/sales/${saleHash}/change-currency`, { currency_id: currencyId });
}

export async function createPickupReservation(saleData: PickupReservationCreate): Promise<SaleDetailRead> {
  return await apiClient.post('/crm/sales/pickup-reservation', saleData);
}

/**
 * Create a new sale
 */
export async function createSale(saleData: SaleCreate): Promise<SaleDetailRead> {
  return await apiClient.post('/crm/sales', saleData);
}

/**
 * Update sale status with validation
 */
export async function updateSaleStatus(saleHash: string, statusData: SaleStatusUpdate): Promise<SaleDetailRead> {
  return await apiClient.patch(`/crm/sales/${saleHash}/status`, statusData);
}

export interface SaleRestoreBlockers {
  /** false when a `blockers` entry makes the restore impossible to honour */
  restorable: boolean;
  allowed_role: boolean;
  /** hard stops: money refunded, part sold on. No way around these. */
  blockers: string[];
  /** document damage (dead AWB, reversed invoice) — restorable, but only with acknowledge:true */
  warnings: string[];
  requires_acknowledgement: boolean;
  target_status: SaleStatus | null;
}

/**
 * Dry-run of the un-cancel checks — lets the UI say WHY a cancelled sale can't
 * be restored (dead AWB, reversed invoice, refunded money, part sold on)
 * before the user bothers typing a reason.
 */
export async function getSaleRestoreBlockers(saleHash: string): Promise<SaleRestoreBlockers> {
  return await apiClient.get(`/crm/sales/${saleHash}/restore-blockers`);
}

/**
 * Un-cancel a sale back into the status it was cancelled from. The target is
 * derived server-side from the activity log — the caller only supplies a reason.
 */
export async function restoreSale(
  saleHash: string,
  reason: string,
  acknowledge = false,
): Promise<SaleDetailRead> {
  return await apiClient.post(`/crm/sales/${saleHash}/restore`, { reason, acknowledge });
}

/**
 * Add a payment to a sale
 */
export async function addPayment(saleHash: string, paymentData: PaymentCreate): Promise<PaymentRead> {
  return await apiClient.post(`/crm/sales/${saleHash}/payments`, paymentData);
}

/**
 * Generate invoice for a sale
 */
export async function generateInvoiceForSale(saleHash: string): Promise<SaleDetailRead> {
    try {
        const response = await apiClient.post(`/crm/sales/${saleHash}/invoice`, null);
        return response;
    } catch (error) {
        console.error(`Failed to generate invoice for sale ${saleHash}:`, error);
        throw error;
    }
}

/**
 * Grant a commercial discount after pricing: raises `discount_amount`, refunds
 * the overpayment (Stripe automatically, otherwise a refund row of the given
 * type) and issues the partial storno in SmartBill. Returns the refreshed sale;
 * SmartBill failures come back on `document_warnings`, never as an error.
 */
export async function applySaleDiscount(saleHash: string, payload: SaleDiscountPayload): Promise<SaleDetailRead> {
  return await apiClient.post(`/crm/sales/${saleHash}/discount`, payload);
}

export async function cancelSaleWithRefund(saleHash: string, payload: SaleCancelRefundPayload): Promise<SaleDetailRead> {
  return await apiClient.post(`/crm/sales/${saleHash}/cancel-refund`, payload);
}

export async function reverseInvoiceForSale(saleHash: string, invoiceId: number): Promise<SaleDetailRead> {
  return await apiClient.post(`/crm/sales/${saleHash}/invoices/${invoiceId}/reverse`, null);
}

/** What "Stornează diferența" would issue: the lines that left the sale since
 * its invoice, or `blocker` when only a full storno + new invoice fits. */
export interface PartialStornoPlan {
  invoice_ref: string | null;
  currency: string;
  invoiced_total: number;
  sale_total: number;
  excess: number;
  lines: { name: string; sku: string | null; quantity: number; unit_price: number; total: number }[];
  blocker: string | null;
}

export async function previewPartialStorno(saleHash: string): Promise<PartialStornoPlan> {
  return await apiClient.get(`/crm/sales/${saleHash}/partial-storno`);
}

/** `expectedAmount` = the difference the operator confirmed; the API refuses another. */
export async function issuePartialStorno(saleHash: string, expectedAmount: number): Promise<SaleDetailRead> {
  return await apiClient.post(`/crm/sales/${saleHash}/partial-storno`, { expected_amount: expectedAmount });
}

export async function addManualInvoice(saleHash: string, invoiceSeries: string, invoiceNumber: number): Promise<SaleDetailRead> {
  return await apiClient.post(`/crm/sales/${saleHash}/invoices/manual`, {
    invoice_series: invoiceSeries,
    invoice_number: invoiceNumber,
  });
}

export type WarrantyLanguage = 'ro' | 'en' | 'fr';

export async function downloadWarrantyCertificate(saleHash: string, lang?: WarrantyLanguage): Promise<Blob> {
  // Without an explicit lang, the backend auto-detects it from the delivery country.
  return await apiClient.get(`/crm/sales/${saleHash}/documents/warranty`, {
    params: lang ? { warranty_lang: lang } : undefined,
    responseType: 'blob'
  });
}

export async function downloadPrintPack(
  saleHash: string,
  opts: { forceReprint?: boolean } = {}
): Promise<Blob> {
  // Combined AWB + invoice + warranty PDF, ready to print. Auto-detects warranty
  // language from the delivery country (backend). Mirrors the AWB reprint 409 flow.
  return await apiClient.get(`/crm/sales/${saleHash}/documents/print-pack`, {
    params: { force_reprint: opts.forceReprint ?? false },
    responseType: 'blob',
    timeoutMs: 60000,
    // Lazily CREATES the AWB/invoice on first call → mutates the sale; mark so
    // this tab doesn't flash its own action (GET, so the verb check misses it).
    marksSelfMutation: true,
  });
}

export async function downloadQuickInvoice(saleHash: string): Promise<Blob> {
  return await apiClient.get(`/crm/sales/${saleHash}/documents/quick-invoice`, {
    responseType: 'blob',
    maxRetries: 1,
    timeoutMs: 60000,
    marksSelfMutation: true, // GET that generates the SmartBill invoice (mutation)
  });
}

export async function downloadQuickAwb(
  saleHash: string,
  format: 'a4' | 'a6' = 'a4',
  forceReprint = false
): Promise<Blob> {
  return await apiClient.get(`/crm/sales/${saleHash}/documents/quick-awb`, {
    params: { format, force_reprint: forceReprint },
    responseType: 'blob',
    maxRetries: 1,
    timeoutMs: 60000,
    marksSelfMutation: true, // GET that lazily creates the courier AWB (mutation)
  });
}

/**
 * Convert an existing sale to reception (pickup) flow: set pickup, optional payment(s), generate invoice, deduct stock
 */
export async function convertSaleToReception(
  saleHash: string,
  payload: { 
    payment_type?: PaymentType | null;  // Deprecated: use payments array
    payments?: Array<{ payment_type: PaymentType; amount: number }> | null;
  } = {}
): Promise<SaleDetailRead> {
  return await apiClient.post(`/crm/sales/${saleHash}/convert-to-reception`, payload);
}

export async function collectPickupSaleInReception(
  saleHash: string,
  payload: {
    payment_type?: PaymentType | null;
    payments?: Array<{ payment_type: PaymentType; amount: number }> | null;
  } = {}
): Promise<SaleDetailRead> {
  return await apiClient.post(`/crm/sales/${saleHash}/collect-in-reception`, payload, {
    maxRetries: 1,
    timeoutMs: 5 * 60 * 1000,
  });
}

export interface SaleStatusNavResponse {
  total: number;
  index: number; // zero-based
  prev_sale_id?: string | null;
  next_sale_id?: string | null;
  sort_by: 'created_at' | 'updated_at';
  sort_order: 'asc' | 'desc';
  status: string;
}

export async function getSaleStatusNav(
  saleHash: string,
  params: { sort_by?: 'created_at' | 'updated_at'; sort_order?: 'asc' | 'desc'; status?: string } = {}
): Promise<SaleStatusNavResponse> {
  return await apiClient.get(`/crm/sales/${saleHash}/status-nav`, { params });
}

/**
 * Get CRM activities for a sale by hash via flexible entity endpoint
 */
export async function getSaleActivities(
  saleHash: string,
  params: { limit?: number; offset?: number; count_only?: boolean } = {}
): Promise<{ total: number; items: any[]; limit: number; offset: number } | { count: number }> {
  return await apiClient.get(`/crm/activities/entity/${saleHash}`, { params });
}

/**
 * Composite update for sale: batch-edit line items and optional status, returns full sale
 */
export async function compositeUpdateSale(
  saleHash: string,
  payload: SaleCompositeUpdatePayload,
  images?: File[]
): Promise<SaleDetailRead> {
  if (images && images.length > 0) {
    const formData = new FormData();
    formData.append('payload', JSON.stringify(payload));
    images.forEach((file, idx) => {
      const namedFile = file instanceof File ? file : new File([file as Blob], `upload-${idx + 1}.jpg`, { type: (file as Blob)?.type || 'image/jpeg' });
      formData.append('files', namedFile);
    });
    // Long-running media processing: no retries, extended timeout
    return await apiClient.postFormData(`/crm/sales/${saleHash}/composite-update`, formData, { maxRetries: 0, timeoutMs: 5 * 60 * 1000 });
  }
  return await apiClient.post(`/crm/sales/${saleHash}/composite-update`, payload);
}

/**
 * Get sales for a specific contact with nested details
 */
export async function getContactSales(
  contactHash: string,
  params: { limit?: number; offset?: number } = {}
): Promise<PaginatedSales> {
  const response = await apiClient.get('/crm/sales', {
    params: { ...params, contact_id: contactHash, include_line_items: true, include_shipment_payment_invoice: true }
  });
  return response as PaginatedSales;
}

export async function getCompanySales(
  companyHash: string,
  params: { limit?: number; offset?: number } = {}
): Promise<PaginatedSales> {
  const response = await apiClient.get('/crm/sales', {
    params: { ...params, company_id: companyHash, include_line_items: true, include_shipment_payment_invoice: true }
  });
  return response as PaginatedSales;
}

/**
 * Get sales count for a specific contact
 */
export async function getSalesCountByContact(contactHash: string): Promise<number> {
  const result = await listSales({ contact_id: contactHash, count_only: true });
  
  if (result && 'count' in result && typeof result.count === 'number') {
    return result.count;
  }
  
  if (result && 'total' in result && typeof result.total === 'number') {
    return result.total;
  }
  
  console.error('Invalid sales count response:', result);
  return 0;
}

export async function getSalesCountByCompany(companyHash: string): Promise<number> {
  const result = await listSales({ company_id: companyHash, count_only: true });

  if (result && 'count' in result && typeof result.count === 'number') {
    return result.count;
  }

  if (result && 'total' in result && typeof result.total === 'number') {
    return result.total;
  }

  console.error('Invalid company sales count response:', result);
  return 0;
}

// Utility functions for frontend

/**
 * Get payment type label
 */
export function getPaymentTypeLabel(type: PaymentType): string {
  const labels: Record<PaymentType, string> = {
    [PaymentType.CASH]: 'Numerar',
    [PaymentType.CARD_POS]: 'Card POS',
    [PaymentType.CARD_ONLINE]: 'Card online',
    [PaymentType.BANK_TRANSFER]: 'Transfer Bancar',
    [PaymentType.CASH_ON_DELIVERY]: 'Ramburs la livrare'
  };
  return labels[type] || type;
}

/**
 * Get sale channel label
 */
export function getFulfillmentMethodLabel(fulfillmentMethod: FulfillmentMethod): string {
  const labels: Record<FulfillmentMethod, string> = {
    [FulfillmentMethod.PICKUP_IN_STORE]: 'Recepție',
    [FulfillmentMethod.DELIVERY]: 'Comandă'
  };
  return labels[fulfillmentMethod] || fulfillmentMethod;
}

/**
 * Format currency amount. Optionally provide a currency code (e.g., 'RON', 'EUR').
 * Defaults to 'RON' for backwards compatibility.
 */
export function formatCurrency(amount: number, currency: string = 'RON'): string {
  return new Intl.NumberFormat('ro-RO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Calculate totals from line items
 */
export function calculateSaleTotals(lineItems: LineItemFullRead[]): {
  subtotal: number;
  itemCount: number;
} {
  const subtotal = lineItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const itemCount = lineItems.reduce((count, item) => count + item.quantity, 0);
  
  return { subtotal, itemCount };
}

/**
 * Did the customer pay for shipping before the parcel left?
 *
 * A dezShop Europa order (EUR) charged the card at checkout; an RO order paid
 * by card online, bank transfer, or cash / card POS at receptie. The rest pay
 * ramburs, at the door. See `customerShippingCost` for what each one pays.
 *
 * Mirror of `paid_before_dispatch_sale_ids` in the backend
 * (app/stoc/services/shipping_price.py). Change one, change the other.
 */
export function saleWasPaidUpFront(sale: { currency_id?: number; payments?: Array<{ payment_type?: string; status?: string }> }): boolean {
  if (sale?.currency_id === 3) return true;
  const notCollected = ['reimbursed', 'charged_back', 'cancelled', 'error'];
  return (sale?.payments || []).some(p => {
    const type = (p?.payment_type || '').toLowerCase() as PaymentType;
    const status = (p?.status || '').toLowerCase();
    return SETTLED_BEFORE_DISPATCH_PAYMENT_TYPES.has(type) && notCollected.indexOf(status) === -1;
  });
}

/**
 * The shipping price the customer pays for one shipment.
 *
 * `estimated` is the price quoted to the customer; `actual` is what the courier
 * charges us, known once the AWB exists. Paid before dispatch → the quote, and
 * a different courier price is ours. Ramburs → the higher of the two: a courier
 * price above the quote is collected at the door.
 *
 * Mirror of `customer_shipping_cost` in the backend
 * (app/stoc/services/shipping_price.py). Change one, change the other.
 */
export function customerShippingCost(
  estimated: number | null | undefined,
  actual: number | null | undefined,
  paidUpFront: boolean
): number | null {
  const est = estimated == null ? null : Number(estimated);
  const act = actual == null ? null : Number(actual);
  if (paidUpFront) return est ?? act;
  if (est == null || act == null) return est ?? act;
  return Math.max(est, act);
}

/**
 * Customer-facing shipping on a sale: cancelled shipments are ignored and
 * supplier-paid shipping is not the customer's to pay.
 */
export function computeCustomerShippingCost(sale: any): number {
  const paidUpFront = saleWasPaidUpFront(sale);
  return (sale?.shipments || [])
    .filter((s: any) => (s?.status || '').toLowerCase() !== 'cancelled')
    .filter((s: any) => (s?.fee_payer || '').toLowerCase() !== 'supplier')
    .reduce((sum: number, s: any) => (
      sum + (customerShippingCost(s?.estimated_shipping_cost, s?.actual_shipping_cost, paidUpFront) || 0)
    ), 0);
}

/**
 * What the customer owes for this sale: items + customer-paid shipping, less
 * swap credits and the commercial discount.
 *
 * Mirror of `_compute_expected_sale_total` (backend). The sale summary shows
 * the same figure broken into its parts; this is the single-number version for
 * guards that only need to compare it against `amount_paid`.
 */
export function computeSaleOrderTotal(sale: any): number {
  const items = (sale?.line_items || []).reduce(
    (sum: number, sli: any) => sum + ((Number(sli?.line_item?.price) || 0) * (Number(sli?.sold_quantity) || 1)),
    0
  );
  const discount = Number(sale?.discount_amount) || 0;
  return items + computeCustomerShippingCost(sale) - computeSwapCredit(sale) - discount;
}

/**
 * What the swap returns on this sale take off its total.
 *
 * Cancelled returns are ignored — those are exchanges the customer backed out
 * of, so nothing came back and nothing is credited. They stay on the page as
 * history; they just don't move money. In-flight ones (new / in_progress /
 * awaiting_*) DO credit: on a swap the exchange is agreed up front.
 *
 * Mirror of the swap-credit query in `_compute_expected_sale_total` (backend).
 * The COD value written on the AWB uses this too — a wrong figure there is
 * cash the courier never collects.
 */
export function computeSwapCredit(sale: any): number {
  return (sale?.swap_returns || [])
    .filter((sr: any) => (sr?.status || '').toLowerCase() !== 'cancelled')
    .reduce((sum: number, sr: any) => sum + (Number(sr?.total) || 0), 0);
}

/**
 * Get next valid statuses for a sale
 */
export function getNextValidStatuses(currentStatus: SaleStatus): SaleStatus[] {
  return VALID_STATUS_TRANSITIONS[currentStatus] || [];
}

/**
 * Check if sale can be edited
 */
export function canEditSale(status: SaleStatus): boolean {
  const notAllowed: SaleStatus[] = [SaleStatus.DELIVERED, SaleStatus.COMPLETED, SaleStatus.CANCELLED];
  return notAllowed.indexOf(status) === -1;
}

/**
 * Check if payments can be added
 */
export function canAddPayment(status: SaleStatus, balanceDue: number): boolean {
  const disallowed: SaleStatus[] = [SaleStatus.CANCELLED];
  return balanceDue > 0 && disallowed.indexOf(status) === -1;
}

// Sales summary types for dashboard
export interface SalesSummaryItem {
  count: number;
  amount_ron: number;
}

export interface SalesSummaryResponse {
  date: string;
  bnr_rate_date: string | null;
  pickup_in_store: SalesSummaryItem;
  delivery: SalesSummaryItem;
  total: SalesSummaryItem;
}

/**
 * Get sales summary for a specific date (dashboard endpoint)
 * Returns counts and amounts by fulfillment method, converted to RON
 */
export async function getSalesSummary(date?: string): Promise<SalesSummaryResponse> {
  const params = date ? { date } : {};
  return await apiClient.get('/crm/dashboard/sales-summary', { params });
}

// Returns summary types for dashboard
export interface ReturnsSummaryItem {
  count: number;
  amount_ron: number;
}

export interface ReturnsSummaryResponse {
  date: string;
  bnr_rate_date: string | null;
  in_store: ReturnsSummaryItem;
  delivery: ReturnsSummaryItem;
  total: ReturnsSummaryItem;
}

/**
 * Get returns summary for a specific date (dashboard endpoint)
 * Returns counts and amounts by return channel, converted to RON
 */
export async function getReturnsSummary(date?: string): Promise<ReturnsSummaryResponse> {
  const params = date ? { date } : {};
  return await apiClient.get('/crm/dashboard/returns-summary', { params });
}

// Created-trends timeseries types and call for the dashboard "Activitate creare" card.
export type CreatedTrendsEntity = 'quotes' | 'contacts' | 'line_items' | 'vehicles' | 'parts';
export type CreatedTrendsPeriod = 'today' | 'yesterday' | 'last_7d' | 'last_14d' | 'last_30d' | 'last_12m';
export type CreatedTrendsGranularity = 'hour' | 'day' | 'month';

export interface CreatedTrendsResponse {
  period: CreatedTrendsPeriod;
  granularity: CreatedTrendsGranularity;
  buckets: string[];
  series: Partial<Record<CreatedTrendsEntity, number[]>>;
}

export async function getCreatedTrends(params: {
  entities: CreatedTrendsEntity[];
  period: CreatedTrendsPeriod;
}): Promise<CreatedTrendsResponse> {
  return await apiClient.get('/crm/dashboard/created-timeseries', {
    params: {
      entities: params.entities.join(','),
      period: params.period,
    },
  });
}

// Sales-per-channel timeseries types and call for the dashboard "Vânzări pe canal" card.
export interface SalesTrendsChannel {
  key: string; // 'direct' or 'p<partner_id>'
  partner_id: number | null;
  name: string;
}

export interface SalesTrendsSeriesEntry {
  count: number[];
  amount_ron: number[];
}

export interface SalesTrendsResponse {
  period: CreatedTrendsPeriod;
  granularity: CreatedTrendsGranularity;
  buckets: string[];
  channels: SalesTrendsChannel[];
  series: Record<string, SalesTrendsSeriesEntry>;
}

export async function getSalesTrends(params: { period: CreatedTrendsPeriod }): Promise<SalesTrendsResponse> {
  return await apiClient.get('/crm/dashboard/sales-timeseries', {
    params: { period: params.period },
  });
}

// Export an aggregated service object to normalize import patterns
export const salesService = {
  listSales,
  getSalesCount,
  getStatusCounts,
  getSaleDetails,
  getSaleStatusNav,
  getSaleActivities,
  createSale,
  updateSaleStatus,
  addPayment,
  createReceptionSale,
  createReceptionSwap,
  createOnlineOrder,
  getPricingContext,
  changeSaleCurrency,
  createPickupReservation,
  getContactSales,
  getCompanySales,
  getSalesCountByContact,
  getSalesCountByCompany,
  getSaleStatusLabel,
  getSaleStatusColor,
  getPaymentTypeLabel,
  getFulfillmentMethodLabel,
  formatCurrency,
  calculateSaleTotals,
  saleWasPaidUpFront,
  customerShippingCost,
  computeCustomerShippingCost,
  computeSwapCredit,
  computeSaleOrderTotal,
  getNextValidStatuses,
  getSaleRestoreBlockers,
  restoreSale,
  canEditSale,
  canAddPayment,
  generateInvoiceForSale,
  applySaleDiscount,
  cancelSaleWithRefund,
  reverseInvoiceForSale,
  previewPartialStorno,
  issuePartialStorno,
  addManualInvoice,
  convertSaleToReception,
  collectPickupSaleInReception,
  downloadWarrantyCertificate,
  downloadPrintPack,
  downloadQuickInvoice,
  downloadQuickAwb,
  compositeUpdateSale,
  getSalesSummary,
  getReturnsSummary,
  getCreatedTrends,
  getSalesTrends,
  listSalesPartners,
};
